import { ChangeLog, Task, User, UserChangeStats } from '../types';
import { generateId } from '../utils/idGenerator';
import { parseDateLike } from '../utils/dateUtils';

/**
 * Change Tracking Service
 * Tracks all changes to tasks, projects, and other entities
 */

export function generateChangeLogId(): string {
  return generateId('change');
}

/**
 * Create a change log entry
 */
export function createChangeLog(params: {
  entityType: ChangeLog['entityType'];
  entityId: string;
  changeType: ChangeLog['changeType'];
  fieldName: string;
  oldValue: string | number | undefined;
  newValue: string | number | undefined;
  changedBy: string;
  changedByName?: string;
  reason?: string;
}): ChangeLog {
  const { entityType, entityId, changeType, fieldName, oldValue, newValue, changedBy, changedByName, reason } = params;

  // Determine impact level based on change type
  const impactLevel = determineImpactLevel(changeType, oldValue, newValue);

  return {
    id: generateChangeLogId(),
    entityType,
    entityId,
    changeType,
    fieldName,
    oldValue: String(oldValue ?? ''),
    newValue: String(newValue ?? ''),
    changedBy,
    changedByName,
    changedAt: new Date().toISOString(),
    reason,
    impactLevel,
  };
}

/**
 * Determine impact level of a change
 */
function determineImpactLevel(
  changeType: ChangeLog['changeType'],
  oldValue: string | number | undefined,
  newValue: string | number | undefined
): 'low' | 'medium' | 'high' | 'critical' {
  switch (changeType) {
    case 'status':
      return 'medium';
    case 'priority':
      if (newValue === 'P0' || newValue === 'Critical') return 'high';
      return 'medium';
    case 'targetDate':
      // If date is moved back significantly, it's high impact
      if (oldValue && newValue) {
        const oldDate = parseDateLike(oldValue);
        const newDate = parseDateLike(newValue);
        if (oldDate && newDate) {
          const diffDays = (newDate.getTime() - oldDate.getTime()) / (1000 * 60 * 60 * 24);
          if (Math.abs(diffDays) > 7) return 'high';
        }
      }
      return 'medium';
    case 'estimate':
      // If estimate changes by more than 50%, it's high impact
      if (oldValue && newValue) {
        const old = Number(oldValue);
        const newer = Number(newValue);
        const percentChange = Math.abs((newer - old) / old) * 100;
        if (percentChange > 50) return 'high';
        if (percentChange > 25) return 'medium';
      }
      return 'low';
    case 'cost':
      // Cost changes are always significant
      return 'high';
    case 'assignee':
      return 'medium';
    case 'budget':
    case 'timeline':
      return 'high';
    case 'complexity':
    case 'phase':
      return 'medium';
    case 'description':
    case 'title':
    case 'category':
    case 'dependencies':
      return 'low';
    default:
      return 'low';
  }
}

/**
 * Track task changes
 */
export function trackTaskChanges(
  oldTask: Task,
  newTask: Task,
  changedBy: string,
  changedByName?: string,
  reason?: string
): ChangeLog[] {
  const changes: ChangeLog[] = [];

  // Track status changes
  if (oldTask.status !== newTask.status) {
    changes.push(
      createChangeLog({
        entityType: 'task',
        entityId: newTask.id,
        changeType: 'status',
        fieldName: 'status',
        oldValue: oldTask.status,
        newValue: newTask.status,
        changedBy,
        changedByName,
        reason,
      })
    );
  }

  // Track priority changes
  if (oldTask.priority !== newTask.priority) {
    changes.push(
      createChangeLog({
        entityType: 'task',
        entityId: newTask.id,
        changeType: 'priority',
        fieldName: 'priority',
        oldValue: oldTask.priority,
        newValue: newTask.priority,
        changedBy,
        changedByName,
        reason,
      })
    );
  }

  // Track target date changes
  if (oldTask.targetCompletionDate !== newTask.targetCompletionDate) {
    changes.push(
      createChangeLog({
        entityType: 'task',
        entityId: newTask.id,
        changeType: 'targetDate',
        fieldName: 'targetCompletionDate',
        oldValue: oldTask.targetCompletionDate,
        newValue: newTask.targetCompletionDate,
        changedBy,
        changedByName,
        reason,
      })
    );
  }

  // Track estimate changes (estimated hours)
  if (oldTask.estimatedHours !== newTask.estimatedHours) {
    changes.push(
      createChangeLog({
        entityType: 'task',
        entityId: newTask.id,
        changeType: 'estimate',
        fieldName: 'estimatedHours',
        oldValue: oldTask.estimatedHours,
        newValue: newTask.estimatedHours,
        changedBy,
        changedByName,
        reason,
      })
    );
  }

  // Track estimate changes (estimated days)
  if (oldTask.estimatedDays !== newTask.estimatedDays) {
    changes.push(
      createChangeLog({
        entityType: 'task',
        entityId: newTask.id,
        changeType: 'estimate',
        fieldName: 'estimatedDays',
        oldValue: oldTask.estimatedDays,
        newValue: newTask.estimatedDays,
        changedBy,
        changedByName,
        reason,
      })
    );
  }

  // Track LLM adjusted hours
  if (oldTask.llmAdjustedHours !== newTask.llmAdjustedHours) {
    changes.push(
      createChangeLog({
        entityType: 'task',
        entityId: newTask.id,
        changeType: 'estimate',
        fieldName: 'llmAdjustedHours',
        oldValue: oldTask.llmAdjustedHours,
        newValue: newTask.llmAdjustedHours,
        changedBy,
        changedByName,
        reason,
      })
    );
  }

  // Track complexity changes
  if (oldTask.complexity !== newTask.complexity) {
    changes.push(
      createChangeLog({
        entityType: 'task',
        entityId: newTask.id,
        changeType: 'complexity',
        fieldName: 'complexity',
        oldValue: oldTask.complexity,
        newValue: newTask.complexity,
        changedBy,
        changedByName,
        reason,
      })
    );
  }

  // Track assignee changes
  const oldAssignees = (oldTask.assigneeIds || []).join(',');
  const newAssignees = (newTask.assigneeIds || []).join(',');
  if (oldAssignees !== newAssignees) {
    changes.push(
      createChangeLog({
        entityType: 'task',
        entityId: newTask.id,
        changeType: 'assignee',
        fieldName: 'assigneeIds',
        oldValue: oldAssignees,
        newValue: newAssignees,
        changedBy,
        changedByName,
        reason,
      })
    );
  }

  // Track title changes
  if (oldTask.title !== newTask.title) {
    changes.push(
      createChangeLog({
        entityType: 'task',
        entityId: newTask.id,
        changeType: 'title',
        fieldName: 'title',
        oldValue: oldTask.title,
        newValue: newTask.title,
        changedBy,
        changedByName,
        reason,
      })
    );
  }

  // Track description changes
  if (oldTask.description !== newTask.description) {
    changes.push(
      createChangeLog({
        entityType: 'task',
        entityId: newTask.id,
        changeType: 'description',
        fieldName: 'description',
        oldValue: oldTask.description?.substring(0, 100),
        newValue: newTask.description?.substring(0, 100),
        changedBy,
        changedByName,
        reason,
      })
    );
  }

  // Track phase changes
  if (oldTask.phase !== newTask.phase) {
    changes.push(
      createChangeLog({
        entityType: 'task',
        entityId: newTask.id,
        changeType: 'phase',
        fieldName: 'phase',
        oldValue: oldTask.phase,
        newValue: newTask.phase,
        changedBy,
        changedByName,
        reason,
      })
    );
  }

  // Track dependencies changes
  if (oldTask.dependencies !== newTask.dependencies) {
    changes.push(
      createChangeLog({
        entityType: 'task',
        entityId: newTask.id,
        changeType: 'dependencies',
        fieldName: 'dependencies',
        oldValue: oldTask.dependencies,
        newValue: newTask.dependencies,
        changedBy,
        changedByName,
        reason,
      })
    );
  }

  // Track category changes
  if (oldTask.category !== newTask.category) {
    changes.push(
      createChangeLog({
        entityType: 'task',
        entityId: newTask.id,
        changeType: 'category',
        fieldName: 'category',
        oldValue: oldTask.category,
        newValue: newTask.category,
        changedBy,
        changedByName,
        reason,
      })
    );
  }

  return changes;
}

/**
 * Update user statistics based on changes
 */
export function updateUserChangeStats(user: User, changes: ChangeLog[]): User {
  const stats: UserChangeStats = user.changeStats || {
    totalChanges: 0,
    dueDateChanges: 0,
    estimateChanges: 0,
    costChanges: 0,
    statusChanges: 0,
  };

  changes.forEach((change) => {
    stats.totalChanges++;
    
    switch (change.changeType) {
      case 'targetDate':
        stats.dueDateChanges++;
        break;
      case 'estimate':
        stats.estimateChanges++;
        break;
      case 'cost':
        stats.costChanges++;
        break;
      case 'status':
        stats.statusChanges++;
        break;
    }
  });

  stats.lastChangeAt = new Date().toISOString();

  return {
    ...user,
    changeStats: stats,
  };
}

/**
 * Get change summary for display
 */
export function getChangeSummary(change: ChangeLog): string {
  const field = change.fieldName;
  const from = change.oldValue || 'none';
  const to = change.newValue || 'none';

  switch (change.changeType) {
    case 'status':
      return `Status changed from "${from}" to "${to}"`;
    case 'priority':
      return `Priority changed from "${from}" to "${to}"`;
    case 'targetDate':
      return `Target date changed from ${formatDate(from)} to ${formatDate(to)}`;
    case 'estimate':
      if (field === 'estimatedHours') {
        return `Estimated hours changed from ${from}h to ${to}h`;
      } else if (field === 'estimatedDays') {
        return `Estimated days changed from ${from}d to ${to}d`;
      } else if (field === 'llmAdjustedHours') {
        return `LLM adjusted hours changed from ${from}h to ${to}h`;
      }
      return `Estimate changed from ${from} to ${to}`;
    case 'cost':
      return `Cost changed from $${from} to $${to}`;
    case 'assignee':
      return `Assignees changed`;
    case 'complexity':
      return `Complexity changed from "${from}" to "${to}"`;
    case 'title':
      return `Title changed`;
    case 'description':
      return `Description updated`;
    case 'phase':
      return `Phase changed from "${from}" to "${to}"`;
    case 'dependencies':
      return `Dependencies updated`;
    case 'category':
      return `Category changed from "${from}" to "${to}"`;
    default:
      return `${field} changed from "${from}" to "${to}"`;
  }
}

function formatDate(dateStr: string): string {
  if (!dateStr || dateStr === 'none') return 'none';
  const parsedDate = parseDateLike(dateStr);
  return parsedDate ? parsedDate.toLocaleDateString() : dateStr;
}

/**
 * Get impact level badge color
 */
export function getImpactColor(level?: string): string {
  switch (level) {
    case 'critical':
      return 'bg-destructive/10 text-destructive';
    case 'high':
      return 'bg-chart-5/10 text-chart-5';
    case 'medium':
      return 'bg-chart-5/10 text-chart-5';
    case 'low':
      return 'bg-primary/10 text-primary';
    default:
      return 'bg-muted/40 text-muted-foreground';
  }
}
